/**
 * Whether a running replay still owns the chart's viewport, and where to put it if it does.
 *
 * The replay's play loop right-aligns the chart on every bar it reveals, which is right while
 * the user is watching the playhead and wrong the moment they are not: a walkthrough that keeps
 * running when the user scrolls back (see useBacktestChartState's `pauseOnChartNavigation`)
 * would otherwise erase the scroll within 100ms, and re-impose the timeframe's default zoom on
 * top of it.
 *
 * Pure and engine-free on purpose — the two things it reads are numbers the caller has already
 * pulled off the chart handle, so the whole policy is testable without a canvas.
 */

/** Inclusive index range, matching the rest of the replay path. */
export interface FollowRange {
  from: number
  to: number
}

/**
 * How far the newest revealed bar may drift out of view before the replay stops carrying the
 * viewport with it.
 *
 * Two bars, not zero: the engine pans by PIXELS, so an unintended nudge moves the range by a
 * fraction of a bar and must not read as "I want to look back". A pan that means it goes
 * further than this on its first frame.
 */
export const FOLLOW_SLACK_BARS = 2

export interface FollowInput {
  /** What the play loop asked for: right-aligned on the bar it just revealed. */
  requested: FollowRange
  /** What the chart is showing, in CLOSED-bar indices, or null before it has data. */
  current: FollowRange | null
  /** Index of the newest revealed bar, in the same indexing as `current`. */
  newestIdx: number
  /** Bars on screen at the current zoom, or null when the engine cannot say. */
  barCount: number | null
  /** `barCount` at the previous tick, or null on the first. */
  prevBarCount: number | null
  /** Whether the replay was still following as of the previous tick. */
  following: boolean
}

export interface FollowDecision {
  following: boolean
  /** The range to apply, or null to leave the user's view exactly where it is. */
  range: FollowRange | null
}

/**
 * Decide whether the replay still owns the viewport, and where to put it if it does.
 *
 * The follow flag is a POSITION, re-derived from the engine every tick rather than remembered
 * from a gesture. That is what makes it survive the index shifts a head-prune or a history page
 * produces: both move the playhead and the user's view by the same amount, so the distance
 * between them — the only thing this reads — is unchanged, and there is nothing to invalidate.
 *
 * A ZOOM IS NOT A DECISION ABOUT WHERE TO LOOK. klinecharts zooms about the cursor, so zooming
 * in on the middle of the chart pushes the newest bar off the right edge; a position test alone
 * cannot tell that from a deliberate pan. The on-screen bar count is the honest signal — zoom is
 * the only user gesture that changes it, which also means this catches wheel, pinch and
 * time-axis drag identically, where typing the DOM gesture would miss the last two. So a tick
 * that saw the count change keeps the previous verdict and only re-frames.
 */
export function nextFollow(input: FollowInput): FollowDecision {
  const { requested, current, newestIdx, barCount, prevBarCount } = input
  let following = input.following

  if (current && newestIdx >= 0) {
    const zoomed = prevBarCount !== null && barCount !== null && barCount !== prevBarCount
    if (!zoomed) following = current.to >= newestIdx - FOLLOW_SLACK_BARS
  }

  if (!following) return { following, range: null }

  // The user's span, not the timeframe's: re-applying getCandleCountForTimeframe would undo
  // their zoom within one tick, which is the whole reason not pausing was not enough on its own.
  const span = barCount ?? requested.to - requested.from
  return { following, range: { from: Math.max(0, requested.to - span), to: requested.to } }
}
